/**
 * API Client Configuration
 * Handles all HTTP requests with interceptors, error handling, and token management
 */

export interface AppConfig {
  apiBaseUrl: string;
  appName?: string;
  version?: string;
  aiToken?: string;
  paymentMode?: "sandbox" | "production";
}

const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://orderapi.pharmasoft.biz/api";

export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private configLoaded: boolean = false;
  private fullConfig: AppConfig | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Load token from localStorage on initialization
    this.token = localStorage.getItem("auth_token");
  }

  /**
   * Load configuration from public/config.json
   */
  async loadConfig(): Promise<AppConfig> {
    if (this.configLoaded && this.fullConfig) {
      return this.fullConfig;
    }

    try {
      const response = await fetch("/config.json");
      if (response.ok) {
        const config: AppConfig = await response.json();
        if (config.apiBaseUrl) {
          // Normalize base URL - remove trailing slash
          this.baseURL = config.apiBaseUrl.endsWith("/") 
            ? config.apiBaseUrl.slice(0, -1) 
            : config.apiBaseUrl;
          // Store full config including aiToken and other fields
          this.fullConfig = { ...config, apiBaseUrl: this.baseURL };
          this.configLoaded = true;
          return this.fullConfig;
        }
      }
    } catch (error) {
      console.warn("Failed to load config.json, using default API URL:", error);
    }

    // Fallback to default or env variable
    this.baseURL = DEFAULT_API_BASE_URL;
    this.fullConfig = { apiBaseUrl: this.baseURL };
    this.configLoaded = true;
    return this.fullConfig;
  }

  /**
   * Update base URL dynamically
   */
  setBaseURL(url: string) {
    this.baseURL = url;
  }

  /**
   * Get current base URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  /**
   * Set authentication token
   */
  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return this.token || localStorage.getItem("auth_token");
  }

  /**
   * Clear authentication token
   */
  clearToken() {
    this.token = null;
    localStorage.removeItem("auth_token");
  }

  /**
   * Build full URL
   */
  private buildURL(endpoint: string): string {
    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    
    // Remove trailing slash from baseURL if present
    const cleanBaseURL = this.baseURL.endsWith("/") ? this.baseURL.slice(0, -1) : this.baseURL;
    
    return `${cleanBaseURL}/${cleanEndpoint}`;
  }

  /**
   * Build headers with authentication
   */
  private buildHeaders(customHeaders?: HeadersInit): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...customHeaders,
    };

    const token = this.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API errors
   */
  private async handleError(response: Response): Promise<ApiError> {
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText || "An error occurred" };
    }

    const error: ApiError = {
      message: errorData.message || errorData.error || "An error occurred",
      status: response.status,
      errors: errorData.errors,
    };

    // Handle specific error cases
    if (response.status === 401) {
      // Unauthorized - clear token and redirect to login
      this.clearToken();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return error;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = this.buildURL(endpoint);
    const headers = this.buildHeaders(options.headers);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle non-JSON responses
      const contentType = response.headers.get("content-type");
      if (!contentType?.includes("application/json")) {
        if (!response.ok) {
          // For 404 errors, throw ApiError format so it can be caught properly
          if (response.status === 404) {
            throw {
              message: `Resource not found: ${url}`,
              status: 404,
            } as ApiError;
          }
          throw new Error(`HTTP error! status: ${response.status} - URL: ${url}`);
        }
        return {
          data: {} as T,
          success: true,
        };
      }

      const data = await response.json();

      // Check if API returned success: false (even with non-200 HTTP status)
      // This should be checked before response.ok to handle business logic errors
      if (data.success === false) {
        // Return the response but mark it as unsuccessful
        // The calling code should check data.success
        return {
          data: data.data || data,
          message: data.message || "Request failed",
          success: false,
        };
      }

      if (!response.ok) {
        const error = await this.handleError(response);
        throw error;
      }

      return {
        data: data.data || data,
        message: data.message,
        success: data.success !== false,
      };
    } catch (error) {
      // Check if it's an ApiError (has status property)
      if (error && typeof error === "object" && "status" in error) {
        // Don't log 404 errors - they're handled gracefully by the calling code
        const apiError = error as ApiError;
        if (apiError.status !== 404) {
          console.error("API Request Error:", {
            url,
            error: apiError.message,
            status: apiError.status,
            baseURL: this.baseURL,
          });
        }
        throw error;
      }
      const errorMessage = error instanceof Error ? error.message : "Network error occurred";
      console.error("API Request Error:", {
        url,
        error: errorMessage,
        baseURL: this.baseURL,
      });
      throw {
        message: errorMessage,
        status: 0,
      } as ApiError;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, String(v)));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
      url += `?${searchParams.toString()}`;
    }
    return this.request<T>(url, { method: "GET" });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  /**
   * Upload file
   */
  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    const token = this.getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const url = this.buildURL(endpoint);
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await this.handleError(response);
      throw error;
    }

    const data = await response.json();
    return {
      data: data.data || data,
      message: data.message,
      success: data.success !== false,
    };
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient(DEFAULT_API_BASE_URL);

// Load config on initialization
apiClient.loadConfig().catch((error) => {
  console.error("Error loading API configuration:", error);
});

